import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Activity, Zap, Pause, Play, FlaskConical } from "lucide-react";
import api from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";

const HEALTH_STYLE = {
  ACCESSIBLE: "text-emerald border-emerald", SEARCH_ONLY: "text-cyan border-cyan",
  DEGRADED: "text-amber border-amber", AUTO_PAUSED: "text-amber border-amber",
  PARTIAL: "text-amber border-amber", DEAD: "text-crimson border-crimson",
  BLOCKED: "text-crimson border-crimson", UNKNOWN: "text-admmuted border-admborder",
};
const TIER_LABEL = { 1: "T1 · Primary", 2: "T2 · Media", 3: "T3 · Discovery", 4: "T4 · Background" };

export default function Sources() {
  const [items, setItems] = useState([]);
  const [radar, setRadar] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => {
    api.get("/admin/sources").then((r) => setItems(r.data || [])).catch(() => {});
    api.get("/agent/radar").then((r) => setRadar(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (s) => { await api.put(`/admin/sources/${s.id}`, { active: !s.active }); load(); };
  const del = async (id) => { if (!window.confirm("Remove source?")) return; await api.delete(`/admin/sources/${id}`); load(); };
  const test = async (s) => {
    setBusy(s.id);
    try { const r = await api.post(`/agent/sources/${s.id}/test`); toast.success(`${s.name}: ${r.data.health_status}`); }
    catch { toast.error("Test failed"); } finally { setBusy(""); load(); }
  };
  const save = async () => {
    if (!form.name || !form.domain) return toast.error("Name and domain required");
    await api.post("/admin/sources", form); toast.success("Source added"); setForm(null); load();
  };

  const st = radar?.sources || {};
  const CARDS = [
    ["total", "Total", "text-admtext"], ["active", "Active", "text-emerald"],
    ["healthy", "Healthy (RSS)", "text-emerald"], ["search_only", "Search-Only", "text-cyan"],
    ["degraded", "Degraded", "text-amber"], ["paused", "Paused", "text-amber"],
    ["dead", "Dead", "text-crimson"], ["tier1", "Primary Authorities", "text-crimson"],
    ["media", "Media", "text-cyan"], ["company_newsrooms", "Company Newsrooms", "text-admtext2"],
  ];
  const inp = "w-full bg-admbg border border-admborder px-2.5 py-1.5 text-admtext text-sm outline-none focus:border-cyan";

  return (
    <AdminLayout title="Source Control Center">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5" data-testid="source-intelligence-stats">
        {CARDS.map(([k, label, color]) => (
          <div key={k} className="bg-admcard border border-admborder p-3">
            <div className={`text-2xl font-bold font-mono ${color}`}>{st[k] ?? 0}</div>
            <div className="text-[10px] uppercase tracking-wider text-admmuted mt-1">{label}</div>
          </div>
        ))}
      </div>

      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-admtext2">News Radar scans active RSS sources; search-only sources are monitored via query discovery during Research Further.</p>
        <button onClick={() => setForm({ name: "", domain: "", feed_url: "", newsroom_url: "", country: "", category: "", source_type: "INDUSTRY MEDIA", tier: 2, trust_score: 75, discovery_method: "RSS", scan_frequency: "NORMAL", kind: "secondary", priority: 5, active: true })}
          data-testid="add-source-button" className="flex items-center gap-1.5 bg-crimson text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-crimsonhover"><Plus size={14} /> Add Source</button>
      </div>

      {form && (
        <div className="bg-admcard border border-cyan p-5 mb-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="source-form">
          <input placeholder="Name" className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input placeholder="Domain" className={inp} value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} />
          <input placeholder="Feed URL (RSS)" className={inp} value={form.feed_url} onChange={(e) => setForm({ ...form, feed_url: e.target.value })} />
          <input placeholder="Newsroom URL" className={inp} value={form.newsroom_url} onChange={(e) => setForm({ ...form, newsroom_url: e.target.value })} />
          <input placeholder="Country" className={inp} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
          <input placeholder="Category" className={inp} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          <select className={inp} value={form.discovery_method} onChange={(e) => setForm({ ...form, discovery_method: e.target.value })}>{["RSS", "ATOM", "NEWSROOM", "DOMAIN_SEARCH", "QUERY_SEARCH"].map((x) => <option key={x} value={x}>{x}</option>)}</select>
          <select className={inp} value={form.scan_frequency} onChange={(e) => setForm({ ...form, scan_frequency: e.target.value })}>{["HIGH", "NORMAL", "LOW"].map((x) => <option key={x} value={x}>{x}</option>)}</select>
          <select className={inp} value={form.tier} onChange={(e) => setForm({ ...form, tier: +e.target.value })}>{[1, 2, 3, 4].map((x) => <option key={x} value={x}>Tier {x}</option>)}</select>
          <input type="number" placeholder="Trust 0-100" className={inp} value={form.trust_score} onChange={(e) => setForm({ ...form, trust_score: +e.target.value })} />
          <div className="col-span-2 md:col-span-4 flex gap-2">
            <button onClick={save} className="bg-cyan text-admbg px-4 py-1.5 text-xs font-bold uppercase">Save</button>
            <button onClick={() => setForm(null)} className="border border-admborder text-admtext2 px-4 py-1.5 text-xs uppercase">Cancel</button>
          </div>
        </div>
      )}

      <div className="bg-admcard border border-admborder overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-admmuted text-xs uppercase tracking-wider border-b border-admborder">
            <th className="px-5 py-3">Source</th><th className="px-3 py-3">Tier / Type</th><th className="px-3 py-3">Country</th>
            <th className="px-3 py-3">Method</th><th className="px-3 py-3">Freq</th><th className="px-3 py-3">Trust</th>
            <th className="px-3 py-3">Health</th><th className="px-3 py-3">Active</th><th className="px-3 py-3">Actions</th>
          </tr></thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-b border-admborder last:border-0" data-testid={`source-row-${s.id}`}>
                <td className="px-5 py-3"><div className="text-admtext">{s.name}</div><div className="text-xs text-admmuted font-mono">{s.domain}</div></td>
                <td className="px-3 py-3"><div className="text-admtext2 text-xs">{TIER_LABEL[s.tier] || `T${s.tier}`}</div><div className="text-[10px] text-admmuted">{s.source_type}</div></td>
                <td className="px-3 py-3 text-admtext2">{s.country}</td>
                <td className="px-3 py-3 text-admtext2 text-xs">{s.discovery_method}</td>
                <td className="px-3 py-3 text-admtext2 text-xs">{s.scan_frequency}</td>
                <td className="px-3 py-3 font-mono text-admtext2">{s.trust_score ?? s.trust_level}</td>
                <td className="px-3 py-3"><span className={`text-[10px] font-bold uppercase tracking-wider border px-1.5 py-0.5 ${HEALTH_STYLE[s.health_status] || HEALTH_STYLE.UNKNOWN}`}>{(s.health_status || "unknown").replace("_", " ")}</span></td>
                <td className="px-3 py-3"><button onClick={() => toggle(s)} data-testid={`source-toggle-${s.id}`} className={`w-9 h-5 rounded-full relative ${s.active ? "bg-emerald" : "bg-admborder"}`}><span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${s.active ? "left-4" : "left-0.5"}`} /></button></td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => test(s)} disabled={busy === s.id} data-testid={`source-test-${s.id}`} title="Test source" className="text-cyan hover:opacity-80 disabled:opacity-40"><FlaskConical size={14} /></button>
                    <button onClick={() => del(s.id)} title="Delete" className="text-crimson hover:opacity-80"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
